import moment from 'moment';
import { useParams } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner, Tooltip } from 'reactstrap';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useGuaranteedPrice } from '../../hooks/useGuaranteedPrice/useGuaranteedPrice';
import { t } from '../common/Translate';
import { NRM_SUB_STATUS } from '../../config';
import { useCounterAppraisal } from '../../hooks/useCalendar/useRecordBlocs/useCounterAppraisal';
import { currencySign } from '../../Utils/currencySign';
import NCOrderLinkModel from './NCOrderLinkModal';
import { Auth } from '../../config/configuration/react';

export const GuaranteedInformation = ({ toggle, supervisor }) => {
  const { recordId } = useParams();
  const {
    guaranteedPrice,
    firstTooltipOpen,
    setFirstTooltipOpen,
    nrmStatusColor,
    ncOrder,
    getValuationDataByStatus,
    displayNrmInfos,
    isNrmUser,
    isSmartContract,
    isSolContract,
    nrmStatusId,
    nrmStatusLabel,
    historyOpen,
    setHistoryOpen,
    toggleHistory,
    isAllowedToLinkNcOrders,
    originId,
  } = useGuaranteedPrice(recordId);
  const { initialize, counterInspection, isLoading } = useCounterAppraisal({ recordId });
  const {
    tradeInWithOrderId,
    tradeInCancelled,
    counterAppraisalVerification,
    tradeInMaintainedPriceMaintained,
    tradeInMaintainedPriceModified,
    waitingForCounterAppraisal,
  } = NRM_SUB_STATUS;
  const keepCarDecisionDateFormatted = moment(supervisor?.keepCarDecisionDate?.date).format('DD/MM/YYYY HH:mm:ss');

  return (
    <div className="overflow-auto">
      <div className="d-flex align-items-center">
        <p className="text-primary font-weight-bold mr-3">{t('statusGuaranteedPrice')}</p>
        <p data-testid={nrmStatusLabel} className={`${nrmStatusColor(nrmStatusId)} mr-3 px-4 py-1`}>
          {t(nrmStatusLabel)}
        </p>
      </div>
      {displayNrmInfos() && (
        <>
          <div className="d-flex align-items-center">
            <p className="font-weight-bold mr-3">{t('ncOrderLinked')} : </p>
            <p>{guaranteedPrice?.ncOrderLinked === true ? 'Yes' : 'No'}</p>
          </div>
          {getValuationDataByStatus().price !== null && (
            <div className="d-flex align-items-center">
              <div className="mr-1 d-flex align-items-center tooltip-margin">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  size="1x"
                  id="toolTipGuaranteedInformation"
                  className="info-icon mt-1"
                  onClick={() => setFirstTooltipOpen((prev) => !prev)}
                />
                <Tooltip
                  placement="bottom"
                  isOpen={firstTooltipOpen}
                  target="toolTipGuaranteedInformation"
                  toggle={() => setFirstTooltipOpen((prev) => !prev)}
                >
                  {t(`priceDescription`)}
                </Tooltip>
              </div>
              <p className="font-weight-bold mr-3">{t('price')} : </p>
              <p data-testid={`price-${getValuationDataByStatus().price}`}>
                {currencySign(getValuationDataByStatus().price >= 0 ? getValuationDataByStatus().price : 0)}
              </p>
            </div>
          )}
          {ncOrder() !== null && (
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mr-3">{t('ncOrder')} : </p>
              <p>{ncOrder()}</p>
            </div>
          )}
          {guaranteedPrice.validUntil !== null && (
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mr-3">{t('guaranteeValidUntil')} : </p>
              <p>{moment(guaranteedPrice.validUntil.date).format('DD/MM/YYYY')}</p>
            </div>
          )}
          {getValuationDataByStatus().date !== null && (
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mr-3">{t('expectedEntryStockDate')} : </p>
              <p>{moment(getValuationDataByStatus().date.date).format('DD/MM/YYYY')}</p>
            </div>
          )}
        </>
      )}
      {guaranteedPrice.comment !== null && ![1].includes(nrmStatusId) && (
        <div className="d-flex align-items-center max-content">
          <p className="font-weight-bold mr-3">{t('vinPageCommentToDealer')} : </p>
          <p>{guaranteedPrice.comment}</p>
        </div>
      )}

      {nrmStatusId >= 30 && (originId === 10 || originId === 11) && (
        <>
          <div className="d-flex align-items-center max-content">
            <p className="font-weight-bold mr-3">{t('forecastedArrivalAtDealershipDate')} :</p>
            <p>
              {getValuationDataByStatus().dealershipCompoundExpectedDate?.date
                ? moment(getValuationDataByStatus().dealershipCompoundExpectedDate?.date).format('DD/MM/YYYY')
                : t('no_info')}
            </p>
          </div>
          <div className="d-flex align-items-center max-content">
            <p className="font-weight-bold mr-3">{t('arrivalAtDealershipDate')} :</p>
            <p>
              {getValuationDataByStatus().dealershipCompoundRealDate?.date
                ? moment(getValuationDataByStatus().dealershipCompoundRealDate?.date).format('DD/MM/YYYY')
                : t('no_info')}
            </p>
          </div>
          <div className="d-flex align-items-center max-content">
            <p className="font-weight-bold mr-3">{t('appointmentDeliveryDate')} :</p>
            <p>
              {getValuationDataByStatus().customerAppointmentDeliveryDate?.date
                ? moment(getValuationDataByStatus().customerAppointmentDeliveryDate?.date).format('DD/MM/YYYY')
                : t('no_info')}
            </p>
          </div>
        </>
      )}

      {(isSmartContract || isSolContract) && isAllowedToLinkNcOrders && !Auth.roleViewerAccess() && (
        <>
          <p>{t('requestNcOrderLink')}</p>
          <button type="button" className="btn btn-1 custom-padding" onClick={() => setHistoryOpen(true)}>
            <span className="p-5">{t('requestNcOrderLinkYes')}</span>
          </button>
        </>
      )}
      <NCOrderLinkModel isOpen={historyOpen} toggle={toggleHistory} />
      {isNrmUser && !Auth.roleViewerAccess() && (
        <>
          {nrmStatusId === tradeInWithOrderId &&
            supervisor.keepCar === null &&
            supervisor.keepCarDecisionDate !== null && (
              <div className="d-flex align-items-center">
                <p className="font-weight-bold mr-3">{t('PreEmptedOption')} : </p>
                <button type="button" className="anchor-link" onClick={toggle}>
                  {t('makeChoice')}
                </button>
              </div>
            )}
          {nrmStatusId === tradeInWithOrderId && supervisor.keepCar === false && (
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mr-3">{t('PreEmptedOption')} : </p>
              <p>
                {t('keepCarOptionExpired')} {keepCarDecisionDateFormatted}
              </p>
            </div>
          )}
          {nrmStatusId === tradeInCancelled && supervisor.keepCar === true && (
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mr-3">{t('PreEmptedOption')} : </p>
              <p>
                {t('requestCancelledGuarantee')} {keepCarDecisionDateFormatted}
              </p>
            </div>
          )}
        </>
      )}
      {nrmStatusId === waitingForCounterAppraisal && !counterInspection.recordId && !Auth.roleViewerAccess() && (
        <div className="">
          <span className="font-weight-bold mr-3">{t('counterAppraisalReady')} : </span>
          {(!isLoading && (
            <button type="button" className="border-0 bg-transparent" onClick={(e) => initialize(e)}>
              <span className="text-primary text-underline cursor-pointer mr-1">{t('Start')}</span>
            </button>
          )) || <Spinner size="sm" />}
        </div>
      )}
      {[counterAppraisalVerification, tradeInMaintainedPriceMaintained, tradeInMaintainedPriceModified].includes(
        nrmStatusId,
      ) && (
        <div className=" align-items-center">
          {guaranteedPrice.counterAppraisalReadyAt && (
            <>
              <div>
                <span className="font-weight-bold mr-3">{t('counterAppraisalReady')} : </span>
                <span>
                  {t('done')} {moment(guaranteedPrice?.counterAppraisalReadyAt?.date).format('DD/MM/YYYY')}
                </span>
              </div>
              <div className="mt-3">
                <span className="font-weight-bold mr-3">{t('EffectiveDeliveryDate')} : </span>
                <span>
                  {getValuationDataByStatus().customerRealDeliveryDate !== null &&
                    moment(getValuationDataByStatus().customerRealDeliveryDate.date).format('DD/MM/YYYY')}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

GuaranteedInformation.propTypes = {
  toggle: PropTypes.func.isRequired,
  supervisor: PropTypes.oneOfType([PropTypes.array]).isRequired,
};
